import React, { useRef, useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Swiper, SwiperSlide } from 'swiper/react';
import { Pagination } from 'swiper/modules';
import type { Swiper as SwiperType } from 'swiper';

import 'swiper/css';
import 'swiper/css/pagination';

import img1 from '../../../assets/how-it-works/1.png';
import img1safari from '../../../assets/how-it-works/1-safari.png';
import img2 from '../../../assets/how-it-works/2.png';
import img3 from '../../../assets/how-it-works/3.png';
import img4 from '../../../assets/how-it-works/4.png';
import img5 from '../../../assets/how-it-works/5.png';

type Step = {
  image: string;
  key: string;
};

interface CardItemProps {
  step: Step;
  title: string;
  description: string;
}

const CardItem: React.FC<CardItemProps> = ({ step, title, description }) => (
  <div className="bg-white border border-solid border-[#EFE8DF] rounded-[20px] min-h-[503px] max-w-[448px] mx-auto overflow-hidden flex flex-col">
    <div className="h-[300px] flex-shrink-0">
      <img src={step.image} alt={title} className="w-full h-full object-cover" />
    </div>

    <div className="p-5">
      <h3 className="font-medium text-[18px] text-black mb-[11px]">
        {title}
      </h3>

      <p className="text-[16px] text-[#4A4A4A] leading-relaxed">
        {description}
      </p>
    </div>
  </div>
);

const HowItWorks = ({ safari = false }: { safari?: boolean }) => {
  const ns = safari ? 'safari' : 'maldives';
  const { t } = useTranslation(ns);
  const swiperInstanceRef = useRef<SwiperType | null>(null);
  const sliderContainerRef = useRef<HTMLDivElement | null>(null);
  const [isMobile, setIsMobile] = useState<boolean>(false);

  const steps: Step[] = [
    { image: safari ? img1safari : img1, key: 'whatsapp' },
    { image: img2, key: 'call' },
    { image: img3, key: 'proposal' },
    { image: img4, key: 'refine' },
    { image: img5, key: 'secure' },
  ];

  const titleFor = (key: string) => t(`howItWorks.steps.${key}.title`);
  const descFor = (key: string) => t(`howItWorks.steps.${key}.description`);

  useEffect(() => {
    const mediaQuery = window.matchMedia('(max-width: 1099px)');

    const handleMediaChange = (e: MediaQueryListEvent | MediaQueryList) => {
      setIsMobile(e.matches);
    };

    handleMediaChange(mediaQuery);
    mediaQuery.addEventListener('change', handleMediaChange);

    return () => {
      mediaQuery.removeEventListener('change', handleMediaChange);
    };
  }, []);

  useEffect(() => {
    const handleClick = (e: MouseEvent) => {
      if (!swiperInstanceRef.current) return;
      const swiper = swiperInstanceRef.current;
      const clickedSlide = (e.target as HTMLElement).closest('.swiper-slide');

      if (!clickedSlide) return;

      if (clickedSlide.classList.contains('swiper-slide-next')) {
        swiper.slideNext();
      } else if (clickedSlide.classList.contains('swiper-slide-prev')) {
        swiper.slidePrev();
      }
    };

    const sliderElement = sliderContainerRef.current;
    if (sliderElement) {
      sliderElement.addEventListener('click', handleClick);
    }

    return () => {
      if (sliderElement) {
        sliderElement.removeEventListener('click', handleClick);
      }
    };
  }, [isMobile]);

  return (
    <section className="lg:py-[60px] sm:py-10 py-8 bg-white">
      <div className="max-w-[1600px] mx-auto lg:px-10">
        <h2 className="section-heading text-center mb-6 sm:mb-8 md:mb-11">
          {t('howItWorks.heading')}
        </h2>

        {isMobile ? (
          <div ref={sliderContainerRef}>
            <Swiper
              onSwiper={(swiper: SwiperType) => { swiperInstanceRef.current = swiper; }}
              modules={[Pagination]}
              slidesPerView="auto"
              spaceBetween={14}
              slidesOffsetAfter={20}
              slidesOffsetBefore={20}
              breakpoints={{
                640: { slidesOffsetAfter: 24, slidesOffsetBefore: 23, spaceBetween: 20 }
              }}
              pagination={{
                clickable: true,
                el: '.how-it-works-pagination'
              }}

            >
              {steps.map((step) => {
                const title = titleFor(step.key);
                const description = descFor(step.key);
                return (
                  <SwiperSlide key={step.key} className="!w-[70vw] max-w-[240px] sm:max-w-[448px]">
                    <div className="bg-white border border-solid border-[#EFE8DF] rounded-[20px] overflow-hidden flex flex-col min-h-[310px] sm:min-h-[490px]">
                      <div className="h-[147px] sm:h-[300px] flex-shrink-0 overflow-hidden">
                        <img src={step.image} alt={title} className="w-full h-full object-cover" />
                      </div>

                      <div className="p-2.5 sm:p-5">
                        <h3 className="font-medium text-sm sm:text-lg text-black mb-1 sm:mb-[11px]">
                          {title}
                        </h3>

                        <p className="text-xs sm:text-base text-[#4A4A4A] leading-[20px] sm:leading-[29px]">
                          {description}
                        </p>
                      </div>
                    </div>
                  </SwiperSlide>
                );
              })}
            </Swiper>

            <div className="pagination how-it-works-pagination mt-8" />
          </div>
        ) : (
          <>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-[42px] mb-[42px]">
              {steps.slice(0, 3).map((step) => (
                <CardItem key={step.key} step={step} title={titleFor(step.key)} description={descFor(step.key)} />
              ))}
            </div>

            <div className="flex justify-center">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-[42px] w-full md:max-w-[938px]">
                {steps.slice(3, 5).map((step) => (
                  <CardItem key={step.key} step={step} title={titleFor(step.key)} description={descFor(step.key)} />
                ))}
              </div>
            </div>
          </>
        )}
      </div>
    </section>
  );
};

export default HowItWorks;
